import {useState, useEffect, useCallback} from 'react'
import {useParams, useNavigate, useSearchParams, Link, useLocation} from 'react-router-dom'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {Pencil, Play, Trash2, FolderOpen, FolderInput, Clock, Save} from 'lucide-react'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {formatDate} from '@/lib/date-utils'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {CONCEPT_ICON} from '@/lib/concepts'
import {ErrorBanner} from '@/components/ui/error-banner'
import {toast} from '@/hooks/use-toast'
import {ReportDefinitionListItemFields} from '@/types/reporting'
import type {ReportDefinitionDetail as ReportDefinitionDetailType} from '@/types/background-jobs'
import type {SqlExecute} from './types'
import {ReportExecutionStatus} from './types'
import {nextRun} from './cron-utils'
import {CategoryPickerDialog} from './CategoryPickerDialog'
import {ParameterForm} from './ParameterForm'
import {SqlEditorPanel} from '@/components/editors/SqlEditorPanel'
import {ResultTable} from './ResultTable'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {SqlAssistPanel} from './SqlAssistPanel'
import {PageLayout} from '@/components/shared/PageLayout'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {DialogForm} from '@/components/shared/DialogForm'
import {FormField} from '@/components/shared/FormField'
import {DetailField} from '@/components/shared/DetailField'
import {InlineRow} from '@/components/shared/InlineRow'
import {SectionDivider} from '@/components/shared/SectionDivider'
import {SmallText} from '@/components/shared/SmallText'
import {IconText} from '@/components/shared/IconText'
import {Icon} from '@/components/shared/Icon'
import {FormGrid} from '@/components/shared/FormGrid'
import {DetailList} from '@/components/shared/DetailList'
import {MutedText} from '@/components/shared/MutedText'

export function ReportDefinitionDetail() {
    const services = useServices()
    const i18n = useI18n()
    const {id} = useParams<{id: string}>()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const location = useLocation()
    const queryClient = useQueryClient()
    const isCreateMode = !id

    usePageTitle(isCreateMode ? i18n.reporting.reportDetail.createTitle : i18n.reporting.reportDetail.pageTitle)

    const [sql, setSql] = useState('')
    const [savedSql, setSavedSql] = useState('')
    const [runResult, setRunResult] = useState<SqlExecute | null>(null)
    const [runError, setRunError] = useState<Error | null>(null)
    const [isRunning, setIsRunning] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [paramValues, setParamValues] = useState<Record<string, string>>({})

    const [createName, setCreateName] = useState('')
    const [createDescription, setCreateDescription] = useState('')

    const editDialog = useDialog()
    const [editName, setEditName] = useState('')
    const [editDescription, setEditDescription] = useState('')
    const deleteDialog = useDialog()
    const moveDialog = useDialog()

    // Fetch existing query (edit mode)
    const {data: query, isLoading, error} = useQuery<ReportDefinitionDetailType>({
        queryKey: ['reporting-report-detail', id],
        queryFn: () => services.reporting.reportDetail(id!),
        enabled: !!id,
    })

    // Sync SQL from loaded query
    useEffect(() => {
        if (query?.queryDefinition?.sql) {
            setSql(query.queryDefinition.sql)
            setSavedSql(query.queryDefinition.sql)
        }
    }, [query])

    // Handle create mode with location state (runImmediately after create)
    useEffect(() => {
        const state = location.state as {sql?: string; runImmediately?: boolean} | null
        if (state?.sql && id) {
            setSql(state.sql)
            setSavedSql(state.sql)
            if (state.runImmediately) {
                // Clear location state to prevent re-running on navigate back
                navigate(location.pathname, {replace: true, state: null})
                handleRunDirect(id)
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, location.state])

    const updateMutation = useEntityMutation({
        mutationFn: (data: {name?: string; description?: string | null; queryDefinition?: {sql: string}; version: number}) =>
            services.reporting.updateReport(id!, data),
        invalidate: [['reporting-report-detail', id], ['reporting-category-reports']],
        onSuccess: (updatedQuery) => {
            if (updatedQuery.queryDefinition?.sql) {
                setSavedSql(updatedQuery.queryDefinition.sql)
            }
        },
        onError: (err: Error) => {
            toast({variant: 'destructive', description: err.message})
        },
    })

    const deleteMutation = useEntityMutation({
        mutationFn: () => services.reporting.deleteReport(id!),
        invalidate: [['reporting-categories'], ['reporting-category-reports']],
        onSuccess: () => {
            toast({title: i18n.reporting.reportDeleted as string})
            navigate(routes.reporting.categories.path())
        },
        onError: (err: Error) => {
            toast({variant: 'destructive', description: err.message})
        },
    })

    const moveMutation = useEntityMutation({
        mutationFn: (data: {targetCategoryId: string; version: number}) =>
            services.reporting.moveReport(id!, data),
        invalidate: [['reporting-report-detail', id], ['reporting-categories'], ['reporting-category-reports']],
        closeDialog: moveDialog.close,
        onSuccess: () => {
            toast({title: i18n.reporting.reportMoved as string})
        },
        onError: (err: Error) => {
            toast({variant: 'destructive', description: err.message})
        },
    })

    async function handleRunDirect(queryId: string) {
        setIsRunning(true)
        setRunError(null)


        setRunResult(null)
        try {
            const execution = await services.reporting.executeReport(queryId, paramValues)
            if (execution.status === ReportExecutionStatus.FAILED) {
                setRunError(new Error(execution.errorMessage || String(i18n.reporting.runError)))


            } else if (execution.resultData) {
                const columns = execution.resultData.length > 0

                    ? Object.keys(execution.resultData[0]).map(key => ({name: key, type: 'string' as const, label: key}))
                    : []
                setRunResult({
                    columns,
                    rows: execution.resultData,
                    rowCount: execution.rowCount ?? 0,
                    truncated: false,
                })
            }
            queryClient.invalidateQueries({queryKey: ['reporting-report-detail', queryId]})
        } catch (e) {
            setRunError(e instanceof Error ? e : new Error(String(i18n.reporting.runError)))
        } finally {
            setIsRunning(false)
        }
    }

    const handleCreateSave = useCallback(async (andRun: boolean) => {
        const categoryId = searchParams.get(ReportDefinitionListItemFields.categoryId)
        if (!categoryId || !createName.trim()) return
        setIsSaving(true)
        try {
            const created = await services.reporting.createReport(categoryId, {
                    name: createName.trim(),
                    description: createDescription.trim() || null,
                    queryDefinition: {sql: sql.trim() || ''},
            }) as ReportDefinitionDetailType
            // Invalidate so categories view shows the new report
            queryClient.invalidateQueries({queryKey: ['reporting-category-reports']})
            queryClient.invalidateQueries({queryKey: ['reporting-categories']})
            navigate(routes.reporting.reports.detail(created.reportDefinitionId), {
                state: andRun && sql.trim() ? {sql, runImmediately: true} : null,
            })
        } catch (e) {
            toast({variant: 'destructive', description: e instanceof Error ? e.message : String(e)})
        } finally {
            setIsSaving(false)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createName, createDescription, sql, searchParams])

    const handleRun = useCallback(async () => {
        if (isCreateMode) {
            await handleCreateSave(true)
            return
        }

        if (!id || !query) return

        // Edit mode with unsaved SQL changes → save first, then run
        if (sql !== savedSql) {
            setIsSaving(true)
            try {
                await updateMutation.mutateAsync({
                    name: query.name,
                    description: query.description,
                    queryDefinition: {sql},
                    version: query.version,
                })
            } catch (err) {
                setIsSaving(false)
                throw err
            }
            setIsSaving(false)
        }

        await handleRunDirect(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCreateMode, handleCreateSave, id, query, sql, savedSql, paramValues])


    function handleEditSubmit(e: React.FormEvent) {
        e.preventDefault()
        if (!query) return
        updateMutation.mutate({
            name: editName.trim(),
            description: editDescription.trim() || null,
            version: query.version,
        }, {
            onSuccess: () => {
                toast({title: i18n.reporting.reportUpdated as string})
                editDialog.close()
            },
        })
    }

    // Create mode — name/description + SQL editor + save button
    if (isCreateMode) {
        return (
            <PageLayout
                variant="detail"
                icon={CONCEPT_ICON.report}
                title={i18n.reporting.reportDetail.createTitle}
                backTo={true}
                data-testid="report-definition-create-page"
            >
                <Card>
                    <CardContent className="pt-6 space-y-4">
                        <FormGrid columns={2}>
                            <FormField label={i18n.reporting.saveReportDialog.name} htmlFor="create-report-name">
                                <Input
                                    id="create-report-name"
                                    data-testid="input-create-report-name"
                                    value={createName}
                                    onChange={(e) => setCreateName(e.target.value)}
                                    placeholder={i18n.reporting.saveReportDialog.name as string}
                                />
                            </FormField>
                            <FormField label={i18n.reporting.saveReportDialog.description} htmlFor="create-report-description">
                                <Input
                                    id="create-report-description"
                                    data-testid="input-create-report-description"
                                    value={createDescription}
                                    onChange={(e) => setCreateDescription(e.target.value)}
                                    placeholder={i18n.reporting.saveReportDialog.description as string}
                                />
                            </FormField>
                        </FormGrid>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="pt-6 space-y-4">
                        <SqlEditorPanel
                            sql={sql}
                            onChange={setSql}
                            onRun={handleRun}
                            isRunning={isRunning || isSaving}
                        />
                        <SqlAssistPanel
                            onSuggestion={setSql}
                            errorContext={null}
                            currentSql={sql}
                        />
                    </CardContent>
                </Card>

                <InlineRow className="justify-end">
                    <Button
                        data-testid="btn-create-report"
                        size="lg"
                        onClick={() => handleCreateSave(false)}
                        disabled={!createName.trim() || isSaving}
                    >
                        {isSaving
                            ? <><ButtonSpinner/>{i18n.common.saving}</>
                            : <><Icon icon={Save} className="mr-2"/>{i18n.reporting.saveReportDialog.save}</>
                        }
                    </Button>
                </InlineRow>
            </PageLayout>
        )
    }

    return (
        <PageLayout
            variant="detail"
            icon={CONCEPT_ICON.report}
            backTo={routes.reporting.categories.path()}
            loading={isLoading}
            data-testid="report-definition-detail-page"
            error={!isCreateMode ? (error ?? (!isLoading ? notFoundIfMissing(query, 'REPORT_DEFINITION') : undefined)) : undefined}
            title={query?.name ?? ''}
            description={query ? undefined : undefined}
            actions={query ? (
                <>
                    <Button data-testid="btn-run-query" size="sm" onClick={handleRun}
                            disabled={isRunning || !sql.trim()}>
                        {isRunning
                            ? <><ButtonSpinner/>{i18n.common.loading}</>
                            : <><Icon icon={Play} size="xs" className="mr-1"/>{i18n.reporting.runReport}</>}
                    </Button>
                    <Button data-testid="btn-edit-report" variant="outline" size="sm"
                            onClick={() => navigate(routes.reporting.reports.edit(id!))}>
                        <Icon icon={Pencil} size="xs" className="mr-1"/>
                        {i18n.reporting.reportDetail.editReport}
                    </Button>
                    <Button data-testid="btn-move-report" variant="outline" size="sm" onClick={moveDialog.open}>
                        <Icon icon={FolderInput} size="xs" className="mr-1"/>
                        {i18n.reporting.moveReportDialog.title}
                    </Button>
                    <Button data-testid="btn-delete-report" variant="outline" size="sm" onClick={deleteDialog.open}>
                        <Icon icon={Trash2} size="xs" className="mr-1 text-destructive"/>
                        {i18n.reporting.reportDetail.deleteReport}
                    </Button>
                </>
            ) : undefined}
        >
            {query && (<>
            {/* Breadcrumb */}
            <InlineRow gap="md" className="-mt-4 mb-2">
                <Link
                    data-testid="link-breadcrumb-categories"
                    to={routes.reporting.categories.path()}
                    className="hover:text-foreground"
                >
                    <MutedText>{i18n.reporting.categories}</MutedText>
                </Link>
                <MutedText>/</MutedText>
                <Link
                    data-testid="link-breadcrumb-category"
                    to={routes.reporting.categories.path()}
                    className="hover:text-foreground"
                >
                    <MutedText>
                        <IconText icon={FolderOpen} size="xs">{query.categoryName}</IconText>
                    </MutedText>
                </Link>
            </InlineRow>

            {/* Overview card */}
            <Card>
                <CardContent className="pt-6 space-y-4">
                    <DetailList columns={4}>
                        <DetailField
                            label={i18n.reporting.reportDetail.category}
                            value={<IconText icon={FolderOpen} iconClassName="text-muted-foreground">{query.categoryName}</IconText>}
                        />
                        <DetailField
                            label={i18n.reporting.reportDetail.lastRun}
                            value={<IconText icon={Clock} iconClassName="text-muted-foreground">{query.lastRunAt ? formatDate(query.lastRunAt) : i18n.reporting.reportDetail.neverRun}</IconText>}
                        />
                        <DetailField
                            label={i18n.reporting.lastModified}
                            value={formatDate(query.updatedAt)}
                        />
                    </DetailList>
                    {query.description && (
                        <>
                            <SectionDivider/>
                            <Stack className="pt-3">
                                <DetailField
                                    label={i18n.common.description}
                                    value={<SmallText className="whitespace-pre-wrap">{query.description}</SmallText>}
                                    fullWidth
                                />
                            </Stack>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Schedule summary (read-only) */}
            <Card data-testid="card-schedule-summary">
                <CardHeader className="py-3 px-4">
                    <CardTitle className="font-medium" data-testid="card-title-schedule-summary">{i18n.reporting.schedule.title}</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                    {!query.scheduleCron && (query.recipientIdentityIds?.length ?? 0) === 0
                        && (query.recipientEmails?.length ?? 0) === 0
                        && !query.notificationTemplateKey ? (
                        <MutedText data-testid="text-no-schedule">{i18n.reporting.schedule.noSchedule}</MutedText>
                    ) : (
                        <DetailList columns={2}>
                            <DetailField
                                label={i18n.reporting.schedule.rawCron}
                                value={query.scheduleCron
                                    ? <span data-testid="text-schedule-cron"><code>{query.scheduleCron}</code></span>
                                    : <MutedText>{i18n.reporting.schedule.presetNone}</MutedText>}
                            />
                            <DetailField
                                label={i18n.reporting.schedule.nextRun}
                                value={(() => {
                                    const next = query.scheduleCron ? nextRun(query.scheduleCron) : null
                                    return next
                                        ? <span data-testid="text-schedule-next-run">{formatDate(next.toISOString())}</span>
                                        : <MutedText>{i18n.reporting.schedule.nextRunUnset}</MutedText>
                                })()}
                            />
                            <DetailField
                                label={i18n.reporting.schedule.recipientIdentities}
                                value={(query.recipientIdentityIds?.length ?? 0) === 0
                                    ? <MutedText>{i18n.reporting.schedule.recipientIdentitiesEmpty}</MutedText>
                                    : <span data-testid="text-recipient-identities-count">
                                        {i18n.reporting.schedule.identityRecipientCount({count: query.recipientIdentityIds.length})}
                                    </span>}
                            />
                            <DetailField
                                label={i18n.reporting.schedule.recipientEmails}
                                value={(query.recipientEmails?.length ?? 0) === 0
                                    ? <MutedText>{i18n.reporting.schedule.noRecipients}</MutedText>
                                    : <span data-testid="text-recipient-emails">{query.recipientEmails.join(', ')}</span>}
                            />
                            <DetailField
                                fullWidth
                                label={i18n.reporting.schedule.notificationTemplate}
                                value={query.notificationTemplateKey
                                    ? <span data-testid="text-notification-template"><code>{query.notificationTemplateKey}</code></span>
                                    : <MutedText>{i18n.reporting.schedule.defaultTemplate}</MutedText>}
                            />
                        </DetailList>
                    )}
                </CardContent>
            </Card>

            {/* Parameters */}
            {query.parameters?.length > 0 && (
                <Card>
                    <CardContent className="pt-6">
                        <ParameterForm
                            parameters={query.parameters}
                            values={paramValues}
                            onChange={setParamValues}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Results */}
            <Card>
                <CardHeader className="py-3 px-4">
                    <CardTitle className="font-medium" data-testid="card-title-results">
                        {i18n.reporting.reportDetail.results}
                    </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                    <Stack>
                        {runError && (
                            <ErrorBanner error={runError} />
                        )}
                        {!runError && (
                            <ResultTable
                                result={runResult}
                                queryId={id!}
                                isRunning={isRunning}
                            />
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Edit Dialog */}
            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent className="sm:max-w-md">
                    <DialogForm onSubmit={handleEditSubmit}>
                        <DialogHeader>
                            <DialogTitle>{i18n.reporting.editReportDialog.title}</DialogTitle>
                            <DialogDescription>{i18n.reporting.editReportDialog.description}</DialogDescription>
                        </DialogHeader>
                        <Stack className="py-4">
                            <FormField label={i18n.reporting.saveReportDialog.name} htmlFor="edit-report-name">
                                <Input
                                    id="edit-report-name"
                                    data-testid="input-edit-report-name"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                />
                            </FormField>
                            <FormField label={i18n.reporting.saveReportDialog.description} htmlFor="edit-report-description">
                                <Textarea
                                    id="edit-report-description"
                                    data-testid="input-edit-report-description"
                                    value={editDescription}
                                    onChange={(e) => setEditDescription(e.target.value)}
                                    rows={3}
                                />
                            </FormField>
                        </Stack>
                        <SaveCancelFooter
                            testIdPrefix="edit"
                            onCancel={editDialog.close}
                            saving={updateMutation.isPending}
                            disabled={!editName.trim()}
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.isOpen} onOpenChange={deleteDialog.bind.onOpenChange}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{i18n.reporting.reportDetail.deleteReport}</DialogTitle>
                        <DialogDescription>
                            {i18n.reporting.reportDetail.deleteConfirm({name: query.name})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete"
                        onCancel={deleteDialog.close}
                        saving={deleteMutation.isPending}
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                        destructive
                        saveType="button"
                        onSave={() => deleteMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>

            {/* Move Query Picker */}
            <CategoryPickerDialog
                open={moveDialog.isOpen}
                onOpenChange={moveDialog.bind.onOpenChange}
                onSelect={(categoryId) => {
                    if (!categoryId) return
                    moveMutation.mutate({targetCategoryId: categoryId, version: query.version})
                }}
                title={i18n.reporting.moveReportDialog.title as string}
                description={i18n.reporting.moveReportDialog.description as string}
                highlightIds={new Set([query.categoryId])}
            />
        </>)}
        </PageLayout>
    )
}
